import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Search, Phone, Mail, Calendar, Filter, Edit2, Trash2, UserPlus, X } from 'lucide-react';
import { api } from '../lib/api';
import { authService as auth } from '../lib/auth';

const OfflineEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [batches, setBatches] = useState([]);
  const currentUser = auth.getCurrentUser();
  const isManagerOrAdmin = ['admin', 'manager', 'offline_manager'].includes(currentUser?.role?.toLowerCase());
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    course_interested: '',
    source: 'walk-in',
    notes: ''
  });
  
  const [convertData, setConvertData] = useState({
    batch_id: '',
    total_fee: '',
    discount: 0,
    has_lifetime_validity: false,
    lifetime_fee: 0,
    amount_paid: '',
    payment_mode: 'cash',
    utr_number: '',
    next_payment_days: '',
    next_payment_date: ''
  });

  const sources = ['walk-in', 'referral', 'social_media', 'phone_call', 'other'];
  const statuses = ['new', 'contacted', 'interested', 'not_interested', 'converted'];
  const paymentModes = ['cash', 'upi', 'credit_card', 'debit_card'];

  useEffect(() => {
    fetchEnquiries();
    fetchBatches();
  }, [statusFilter, sourceFilter, dateFilter]);

  const fetchEnquiries = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (searchTerm) params.search = searchTerm;
      if (dateFilter) {
        params.start_date = dateFilter;
        params.end_date = dateFilter;
      }
      
      const response = await api.getOfflineEnquiries(params);
      setEnquiries(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch enquiries');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.getOfflineBatches({ active_only: true });
      setBatches(response.data || []);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedEnquiry) {
        const updatePayload = { ...formData };
        // Convert date to ISO string for backend
        if (updatePayload.created_at) {
          updatePayload.created_at = new Date(updatePayload.created_at + 'T00:00:00').toISOString();
        } else {
          delete updatePayload.created_at;
        }
        await api.updateOfflineEnquiry(selectedEnquiry.id, updatePayload);
        toast.success('Enquiry updated successfully');
      } else {
        const { created_at, ...createPayload } = formData;
        await api.createOfflineEnquiry(createPayload);
        toast.success('Enquiry created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchEnquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save enquiry');
    }
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    try {
      const response = await api.convertEnquiryToAdmission(selectedEnquiry.id, convertData);
      toast.success(`Admission created! Number: ${response.data.admission_number}`);
      setShowConvertModal(false);
      setConvertData({ batch_id: '', total_fee: '', discount: 0, has_lifetime_validity: false, lifetime_fee: 0, amount_paid: '', payment_mode: 'cash', utr_number: '', next_payment_days: '', next_payment_date: '' });
      fetchEnquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert enquiry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    try {
      await api.deleteOfflineEnquiry(id);
      toast.success('Enquiry deleted');
      fetchEnquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete enquiry');
    }
  };

  const handleStatusChange = async (enquiry, newStatus) => {
    try {
      await api.updateOfflineEnquiry(enquiry.id, { status: newStatus });
      toast.success('Status updated');
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openEditModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setFormData({
      name: enquiry.name,
      phone: enquiry.phone,
      email: enquiry.email || '',
      course_interested: enquiry.course_interested,
      source: enquiry.source,
      notes: enquiry.notes || '',
      created_at: enquiry.created_at ? enquiry.created_at.split('T')[0] : ''
    });
    setShowModal(true);
  };

  const openConvertModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowConvertModal(true);
  };

  const resetForm = () => {
    setSelectedEnquiry(null);
    setFormData({ name: '', phone: '', email: '', course_interested: '', source: 'walk-in', notes: '' });
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'interested': 'bg-green-100 text-green-800',
      'not_interested': 'bg-red-100 text-red-800',
      'converted': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredEnquiries = enquiries.filter(e => 
    e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.phone?.includes(searchTerm) ||
    e.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="offline-enquiries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Student Enquiries</h1>
          <p className="text-slate-600">Manage student enquiries and convert them to admissions</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
          data-testid="add-enquiry-btn"
        >
          <Plus size={20} />
          New Enquiry
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, phone, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg"
              data-testid="search-enquiries"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
            data-testid="filter-status"
          >
            <option value="">All Status</option>
            {statuses.map(s => (
              <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
            ))}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
            data-testid="filter-source"
          >
            <option value="">All Sources</option>
            {sources.map(s => (
              <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
            data-testid="filter-date"
          />
          {dateFilter && (
            <button
              onClick={() => setDateFilter('')}
              className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200"
              data-testid="clear-date-filter"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Enquiries List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading...</div>
        ) : filteredEnquiries.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No enquiries found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Student</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Contact</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Course</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Source</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEnquiries.map((enquiry) => (
                  <tr key={enquiry.id} className="hover:bg-slate-50" data-testid={`enquiry-row-${enquiry.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{enquiry.name}</div>
                      <div className="text-sm text-slate-500">{enquiry.created_by_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Phone size={14} /> {enquiry.phone}
                      </div>
                      {enquiry.email && (
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <Mail size={14} /> {enquiry.email}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{enquiry.course_interested}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full capitalize">
                        {enquiry.source?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={enquiry.status}
                        onChange={(e) => handleStatusChange(enquiry, e.target.value)}
                        className={`px-2 py-1 text-xs rounded-full border-0 ${getStatusColor(enquiry.status)}`}
                        disabled={enquiry.status === 'converted'}
                      >
                        {statuses.map(s => (
                          <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {new Date(enquiry.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {enquiry.status !== 'converted' && (
                          <button
                            onClick={() => openConvertModal(enquiry)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Convert to Admission"
                            data-testid={`convert-btn-${enquiry.id}`}
                          >
                            <UserPlus size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(enquiry)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(enquiry.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">{selectedEnquiry ? 'Edit Enquiry' : 'New Enquiry'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Course Interested *</label>
                <input
                  type="text"
                  value={formData.course_interested}
                  onChange={(e) => setFormData({...formData, course_interested: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
                <select
                  value={formData.source}
                  onChange={(e) => setFormData({...formData, source: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {sources.map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  rows={3}
                />
              </div>
              {selectedEnquiry && isManagerOrAdmin && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Enquiry Date</label>
                  <input
                    type="date"
                    value={formData.created_at || ''}
                    onChange={(e) => setFormData({...formData, created_at: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    data-testid="edit-enquiry-date"
                  />
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                  {selectedEnquiry ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Convert to Admission Modal */}
      {showConvertModal && selectedEnquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Convert to Admission</h2>
              <button onClick={() => setShowConvertModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 bg-slate-50 border-b">
              <p className="text-sm text-slate-600">Converting enquiry for:</p>
              <p className="font-semibold text-slate-800">{selectedEnquiry.name} - {selectedEnquiry.phone}</p>
            </div>
            <form onSubmit={handleConvert} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Batch *</label>
                <select
                  value={convertData.batch_id}
                  onChange={(e) => setConvertData({...convertData, batch_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required
                >
                  <option value="">Select Batch</option>
                  {batches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Fee Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Total Fee *</label>
                  <input
                    type="number"
                    value={convertData.total_fee}
                    onChange={(e) => setConvertData({...convertData, total_fee: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Discount</label>
                  <input
                    type="number"
                    value={convertData.discount}
                    onChange={(e) => setConvertData({...convertData, discount: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="lifetime"
                  checked={convertData.has_lifetime_validity}
                  onChange={(e) => setConvertData({...convertData, has_lifetime_validity: e.target.checked})}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="lifetime" className="text-sm text-slate-700">Lifetime Validity (Multiple Batches)</label>
              </div>
              {convertData.has_lifetime_validity && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Lifetime Fee</label>
                  <input
                    type="number"
                    value={convertData.lifetime_fee}
                    onChange={(e) => setConvertData({...convertData, lifetime_fee: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              )}
              
              {/* Final Fee Display */}
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-sm text-teal-800">
                  <strong>Fee After Discount:</strong> ₹{(parseFloat(convertData.total_fee) || 0) - (parseFloat(convertData.discount) || 0) + (convertData.has_lifetime_validity ? (parseFloat(convertData.lifetime_fee) || 0) : 0)}
                </p>
              </div>
              
              {/* Payment Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium text-slate-800 mb-3">Payment Details</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Amount Paying Now *</label>
                    <input
                      type="number"
                      value={convertData.amount_paid}
                      onChange={(e) => setConvertData({...convertData, amount_paid: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      placeholder="e.g., 2500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode *</label>
                    <select
                      value={convertData.payment_mode}
                      onChange={(e) => setConvertData({...convertData, payment_mode: e.target.value, utr_number: ''})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      required
                      data-testid="convert-payment-mode"
                    >
                      {paymentModes.map(mode => (
                        <option key={mode} value={mode}>
                          {mode === 'upi' ? 'UPI' : mode === 'credit_card' ? 'Credit Card' : mode === 'debit_card' ? 'Debit Card' : 'Cash'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {convertData.payment_mode && convertData.payment_mode !== 'cash' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-slate-700 mb-1">UTR Number *</label>
                    <input
                      type="text"
                      value={convertData.utr_number}
                      onChange={(e) => setConvertData({...convertData, utr_number: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      placeholder="Transaction reference number"
                      data-testid="convert-utr-input"
                      required
                    />
                  </div>
                )}
                
                {/* Remaining Amount Display */}
                {convertData.amount_paid && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <strong>Remaining Amount:</strong> ₹{Math.max(0, ((parseFloat(convertData.total_fee) || 0) - (parseFloat(convertData.discount) || 0) + (convertData.has_lifetime_validity ? (parseFloat(convertData.lifetime_fee) || 0) : 0)) - (parseFloat(convertData.amount_paid) || 0))}
                    </p>
                  </div>
                )}
                
                {/* Next Payment - Show only if partial payment */}
                {convertData.amount_paid && parseFloat(convertData.amount_paid) < ((parseFloat(convertData.total_fee) || 0) - (parseFloat(convertData.discount) || 0) + (convertData.has_lifetime_validity ? (parseFloat(convertData.lifetime_fee) || 0) : 0)) && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Next Payment In (Days)</label>
                      <input
                        type="number"
                        value={convertData.next_payment_days}
                        onChange={(e) => {
                          const days = parseInt(e.target.value) || 0;
                          const nextDate = new Date();
                          nextDate.setDate(nextDate.getDate() + days);
                          setConvertData({
                            ...convertData, 
                            next_payment_days: e.target.value,
                            next_payment_date: nextDate.toISOString().split('T')[0]
                          });
                        }}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                        placeholder="e.g., 15"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Next Payment Date</label>
                      <input
                        type="date"
                        value={convertData.next_payment_date}
                        onChange={(e) => setConvertData({...convertData, next_payment_date: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowConvertModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                  Create Admission
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineEnquiries;
